import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, Button, Tooltip, notification } from "antd";
import {
  DeleteOutlined,
  PrinterOutlined,
  SendOutlined,
} from "@ant-design/icons";
import moment from "moment";
import styles from "./Note.module.css";
import { CaseNotesDTO } from "../../models/CaseNotes";
import { caseNotesService } from "../../services/caseNotesService";
import { CasesDTO } from "../../models/Cases";
import { useAuth } from "../../contexts/authContext";
import { eventBus } from "../../eventBus";

const { TextArea } = Input;

interface NoteProps {
  caseDetail: CasesDTO;
}

const Note: React.FC<NoteProps> = ({ caseDetail }) => {
  const { loggedDoctor } = useAuth();
  const [caseNotes, setCaseNotes] = useState<CaseNotesDTO[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize] = useState(20);
  const messageContainerRef = useRef<HTMLDivElement>(null);

  const markMessageAsRead = useCallback(async (messageId: number) => {
    
    if (caseDetail.takeInChargeBy?.id !== loggedDoctor!.id && caseDetail.createdBy?.id !== loggedDoctor!.id) {
      return;
    }

    const message = caseNotes.find(note => note.id === messageId);
    if (message && !message.readAt && message.doctor?.id !== loggedDoctor!.id){
      try {
        await caseNotesService.markAsRead(messageId);
        setCaseNotes(prevNotes => 
          prevNotes.map(note => 
            note.id === messageId 
              ? { ...note, readAt: new Date().toISOString() } 
              : note
          )
        );
        eventBus.emit('noteRead');
      } catch (error) {
        console.error("Errore nel marcare il messaggio come letto:", error);
      }
    }
  }, [caseDetail, loggedDoctor, caseNotes]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await caseNotesService.getAllCaseNotes({
          page: currentPage,
          sort: ["createdAt,desc"],
          "caseDetailId.equals": caseDetail.id,
        });
        setCaseNotes((prevNotifications) =>
          currentPage === 0 ? response : [...prevNotifications, ...response]
        );
      } catch (error) {
        console.error("Errore nel caricamento delle notifiche:", error);
        notification.error({
          message: "Errore nel caricamento dei messaggi",
        });
      }
    };

    if (caseDetail) fetchNotifications();
  }, [caseDetail, currentPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = parseInt(entry.target.id.split("-")[1]);
            markMessageAsRead(messageId);
          }
        });
      },
      { threshold: 1.0 }
    );

    if (messageContainerRef.current) {
      const messages =
        messageContainerRef.current.querySelectorAll(".messageBubble");
      messages.forEach((message) => observer.observe(message));
    }

    return () => {
      if (messageContainerRef.current) {
        const messages =
          messageContainerRef.current.querySelectorAll(".messageBubble");
        messages.forEach((message) => observer.unobserve(message));
      }
    };
  }, [caseNotes, markMessageAsRead]);


  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      try {
        setLoading(true);
        const newNotification: CaseNotesDTO = {
          noteContent: newMessage,
          createdAt: moment().toISOString(),
          caseDetail: { id: caseDetail.id } as CasesDTO,
          doctor: { id: loggedDoctor!.id },
        };
        const createdNotification = await caseNotesService.createCaseNote(
          newNotification
        );
        setCaseNotes((prevNotifications) => [
          createdNotification,
          ...prevNotifications,
        ]);
        setNewMessage("");
        notification.success({
          message: "Messaggio inviato",
          description: "Il tuo messaggio è stato inviato correttamente",
        });
      } catch (error) {
        notification.error({
          message: "Errore nell'invio del messaggio",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLoadMore = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrint = () => {
    window.print();
    notification.success({
      message: "Stampa avviata",
    });
  };

  const handleDeleteMessage = async (notificationId: number) => {
    try {
      setLoading(true);
      await caseNotesService.deleteCaseNote(notificationId);
      setCaseNotes((prevNotifications) =>
        prevNotifications.filter(
          (notification) => notification.id !== notificationId
        )
      );
      notification.success({
        message: "Messaggio eliminato con successo",
      });
    } catch (error) {
      console.error("Errore nell'eliminazione del messaggio:", error);
      notification.error({
        message: "Errore nell'eliminazione del messaggio",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.noteContainer}>
      <div className={styles.headerContainer}>
        <h2>Note</h2>
        <Tooltip title="Stampa conversazione">
          <Button icon={<PrinterOutlined />} onClick={handlePrint} />
        </Tooltip>
      </div>

      <div className={styles.messageContainer}>
        {caseNotes.length === (currentPage + 1) * pageSize && (
          <Button onClick={handleLoadMore} style={{ marginBottom: "16px" }}>
            Carica messaggi precedenti
          </Button>
        )}
        <div className={styles.messageContainer} ref={messageContainerRef}>
          {caseNotes.map((notification, index) => {
            const isCurrentUser = notification.doctor?.id === loggedDoctor!.id;
            const isLastMessage = index === 0;
            return (
              <div
                key={notification.id}
                id={`message-${notification.id}`}
                className={`messageBubble ${styles.messageBubble} ${
                  isCurrentUser ? styles.currentUser : styles.otherUser
                }`}
              >
                <div className={styles.messageHeader}>
                  <strong style={{ fontSize: "20px" }}>
                    {isCurrentUser
                      ? "Tu"
                      : `Dott. ${notification.doctor?.user?.login}`}
                  </strong>
                  {isCurrentUser && isLastMessage && (
                    <Tooltip title="Elimina messaggio">
                      <DeleteOutlined
                        onClick={() => handleDeleteMessage(notification.id!)}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          fontSize: "20px",
                          color: "#ff4d4f",
                          transition: "all 0.3s",
                        }}
                        className={styles.deleteIcon}
                      />
                    </Tooltip>
                  )}
                </div>
                <p>{notification.noteContent}</p>
                <div className={styles.messageTimestamp}>
                  {moment(notification.createdAt).format("DD/MM/YYYY HH:mm")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <TextArea
          rows={2}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Scrivi un nuovo messaggio..."
        />
        <Button
          type="primary"
          size="large"
          icon={<SendOutlined />}
          onClick={handleSendMessage}
          disabled={!newMessage}
          loading={loading}
        >
          Invia
        </Button>
      </div>
    </div>
  );
};

export default Note;
